import './context-menu.less';
import { useLatest } from 'ahooks';
import classNames from 'classnames';
import { groupBy } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BaseSystemPlugin,
    checkPointInStage,
    checkPointInViewport,
    convertToStagePoint,
    getBehavior,
    getPointRect,
    getStageIndexByMousePointY,
    useFramework,
    useViewHeight,
    useViewWidth,
} from '@/framework';
import { isLeftButton } from '@/utils/event-kit';
import { IBackgroundLayerProps, IMainLayerProps, IPoint } from '@/version-types';
import addImage from '../components/group-1/add-image';
import backgroundSet from '../components/group-1/background-set';
import backgroundSplit from '../components/group-1/background-split';
import clone from '../components/group-1/clone';
import copy from '../components/group-1/copy';
import cut from '../components/group-1/cut';
import paste from '../components/group-1/paste';
import saveToMySpace from '../components/group-1/save-to-my-space';
import pack from '../components/group-2/pack';
import unpack from '../components/group-2/unpack';
import flip from '../components/group-3/flip';
import lock from '../components/group-3/lock';
import overlap from '../components/group-3/overlap';
import sort from '../components/group-3/sort';
import alignCanvas from '../components/group-4/align-canvas';
import alignInner from '../components/group-4/align-inner';
import deleteItem from '../components/group-4/delete';
import { ContextMenuPlugin } from '../context-menu';
import { getContextMenuState } from '../states/context-menu-state';
import { DEFAULT_SPACE } from '../type';
import MenuItem from './menu-item';

const defaultContextMenuItems = groupBy(
    [
        copy,
        cut,
        paste,
        clone,
        addImage,
        saveToMySpace,
        backgroundSet,
        backgroundSplit,
        pack,
        unpack,
        sort,
        flip,
        overlap,
        lock,
        alignCanvas,
        alignInner,
        deleteItem,
    ],
    'group',
);

const ContextMenu: React.FC = React.memo(() => {
    const framework = useFramework();
    const contextWrapRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    // 右键菜单信息
    const [menuState, setMenuState] = useState<{ isShow: boolean }>({ isShow: false });
    const contextMenuInfoRef = useRef(framework.getInstance(ContextMenuPlugin).positionInfo);
    const activeInfoRef = useRef(framework.getInstance(ContextMenuPlugin).activeInfo);
    const isShowRef = useLatest(menuState.isShow);
    const viewWidth = useViewWidth();
    const viewHeight = useViewHeight();

    useEffect(() => {
        framework.operate.delHoverId();
        framework.operate.hoverExtend.destroy();
        framework.operate.clearSelected();
        framework.getInstance(ContextMenuPlugin).setState(menuState.isShow);
    }, [menuState.isShow]);

    // 销毁右键菜单
    const destroyContextMenu = useCallback(() => {
        if (!isShowRef.current) return;
        menuRef.current?.classList.add('destroy');
        activeInfoRef.current.activeLayer = null;
        activeInfoRef.current.pickLayer = null;
        activeInfoRef.current.isImageEditor = false;
        activeInfoRef.current.operateLayerType = '';
        setMenuState({ isShow: false });
        framework.setShowContextMenu(false);
    }, []);

    // 创建右键菜单
    const createContextMenu = useCallback((point: IPoint) => {
        if (getContextMenuState(framework.identity).disableMenu) return;
        contextMenuInfoRef.current.x = point.x;
        contextMenuInfoRef.current.y = point.y;
        contextMenuInfoRef.current.bottom = 0;
        contextMenuInfoRef.current.right = 0;
        const activeLayer = framework.operate.getActiveLayer();
        const pickLayer = framework.operate.getActivePickLayer();
        const originIndex = framework.stage.getActiveOriginIndex();
        const mainLayer = framework.getMainLayer(originIndex);
        let operateLayerType = '';
        if (activeLayer) {
            const layer = pickLayer || activeLayer;
            operateLayerType = layer.layerType;
        } else {
            const backgroundLayer = framework.getBackgroundLayer(originIndex);
            const backgroundLayerUrl = (backgroundLayer?.data as IBackgroundLayerProps).url;
            //主图不存在（海报编辑器） 或 主图（图片编辑器）存在并且是自定义画布且存在背景
            if (!mainLayer || (mainLayer && (mainLayer.data as IMainLayerProps).isTransparent && backgroundLayerUrl)) {
                operateLayerType = backgroundLayerUrl ? backgroundLayer?.layerType || '' : '';
            } else {
                operateLayerType = mainLayer.layerType;
            }
        }
        activeInfoRef.current.isImageEditor = !!mainLayer;
        activeInfoRef.current.operateLayerType = operateLayerType;
        activeInfoRef.current.activeLayer = activeLayer;
        activeInfoRef.current.pickLayer = pickLayer;
        console.log('context-menu', activeInfoRef.current);
        setMenuState({ isShow: true });
        framework.setShowContextMenu(true);
        setTimeout(() => {
            if (!contextWrapRef.current || !menuRef.current) return;
            const { bottom, right } = contextWrapRef.current.getBoundingClientRect();
            contextMenuInfoRef.current.bottom = bottom;
            contextMenuInfoRef.current.right = right;
            const { width, height } = menuRef.current.getBoundingClientRect();
            let { x, y } = contextMenuInfoRef.current;
            // 处理底部溢出 和 右边溢出的情况
            if (x > right - width) {
                x = right - DEFAULT_SPACE - width;
                contextMenuInfoRef.current.arrowMenuInRight = false;
            } else {
                contextMenuInfoRef.current.arrowMenuInRight = true;
            }
            if (y > bottom - height) {
                y = bottom - DEFAULT_SPACE - height;
            }
            menuRef.current.style.left = `${x}px`;
            menuRef.current.style.top = `${y}px`;
            menuRef.current.classList.remove('destroy');
        }, 0);
    }, []);

    // 监听视图宽高变化，销毁菜单
    useEffect(() => {
        destroyContextMenu();
    }, [viewWidth, viewHeight]);

    // 监听右键点击展示菜单
    useEffect(() => {
        const handler = framework.events.onContextMenu((e: React.MouseEvent) => {
            if (getContextMenuState(framework.identity).disableMenu) return;
            const point = { x: e.pageX, y: e.pageY };
            const hoverId = framework.operate.getHoverId();
            if (hoverId) {
                framework.operate.setActiveLayer(hoverId);
            }
            const index = getStageIndexByMousePointY(framework.identity, point.y);
            framework.stage.setActiveStageIndex(index);
            const inStage = checkPointInStage(framework.identity, point, index);
            if (!inStage) {
                framework.operate.delActiveLayer();
            } else {
                const activeLayer = framework.operate.getActiveLayer();
                if (activeLayer) {
                    const mouseDownRect = getPointRect(framework.identity, point);
                    const layerId = framework.stage.tryPickWrap(mouseDownRect, [activeLayer]);
                    const isInOperateArea = getBehavior(framework.identity, activeLayer)?.checkIsInOperateArea?.(
                        activeLayer.id,
                        point,
                    );
                    if (!layerId && !isInOperateArea) {
                        framework.operate.delActiveLayer();
                    }
                }
            }
            createContextMenu(point);
        });
        return () => {
            framework.events.off(handler);
        };
    }, []);

    // 隐藏菜单组件
    useEffect(() => {
        const handler = framework.events.onMouseDown((e) => {
            if (isLeftButton(e) && isShowRef.current) {
                destroyContextMenu();
                const hoverId = framework.operate.getHoverId();
                if (!hoverId) {
                    return true;
                }
            }
        }, 99);
        const clickHandler = (e: MouseEvent) => {
            const point = { x: e.pageX, y: e.pageY };
            const inViewport = checkPointInViewport(framework.identity, point);
            if (!inViewport && isShowRef.current) {
                destroyContextMenu();
            }
        };
        document.addEventListener('click', clickHandler);
        return () => {
            framework.events.off(handler);
            document.removeEventListener('click', clickHandler);
        };
    }, []);

    // 监听右键菜单关闭事件
    useEffect(() => {
        return framework.getInstance(ContextMenuPlugin).onClose(() => {
            destroyContextMenu();
        });
    }, []);

    // 监听右键菜单打开事件
    useEffect(() => {
        return framework.getInstance(ContextMenuPlugin).onShow((point) => {
            createContextMenu(point);
        });
    }, []);

    /** 获取自定义右键菜单 */
    const clinePoint = contextMenuInfoRef.current;
    const stagePoint = convertToStagePoint(framework.identity, clinePoint, framework.stage.getActiveStageIndex());
    const customContextMenuData = getContextMenuState(framework.identity).getCustomContextMenu({
        stageX: stagePoint.x,
        stageY: stagePoint.y,
        clientX: clinePoint.x,
        clientY: clinePoint.y,
    });
    const customContextMenu: Record<number, React.ReactNode[]> = {};
    if (customContextMenuData) {
        customContextMenuData.forEach((item) => (item.group = item.group || 1));
        const data = groupBy(customContextMenuData, 'group');
        Object.keys(data)
            .map(parseFloat)
            .sort((a, b) => a - b)
            .forEach((group) => {
                customContextMenu[group] = customContextMenu[group] || [];
                data[group].forEach((item, i) => {
                    customContextMenu[group].push(<MenuItem key={`${group}-${i}`} {...item} />);
                });
            });
    }
    /** 默认右键菜单 */
    const defaultContextMenu: Record<number, React.ReactNode[]> = {};
    Object.keys(defaultContextMenuItems)
        .map(parseFloat)
        .sort((a, b) => a - b)
        .forEach((group) => {
            defaultContextMenu[group] = defaultContextMenu[group] || [];
            defaultContextMenuItems[group]
                .sort((a, b) => a.sort - b.sort)
                .forEach((item, i) => {
                    const node = item.render(activeInfoRef.current);
                    if (node) {
                        defaultContextMenu[group].push(<React.Fragment key={`${group}-${i}`}>{node}</React.Fragment>);
                    }
                });
        });

    // console.log('groupData', { customContextMenu, defaultContextMenu });
    const renderMenu = customContextMenuData ? customContextMenu : defaultContextMenu;

    return (
        <BaseSystemPlugin name="context-menu">
            <div className="l-content-menu-wrap" ref={contextWrapRef}>
                <div
                    ref={menuRef}
                    className={classNames('l-content-menu destroy', Object.keys(renderMenu).length ? '' : 'empty')}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {menuState.isShow
                        ? Object.keys(renderMenu).map((group, i) => {
                              return (
                                  <React.Fragment key={group}>
                                      {i && renderMenu[group]?.length ? (
                                          <li className="l-content-menu__line"></li>
                                      ) : null}
                                      {renderMenu[group]?.length ? renderMenu[group] : null}
                                  </React.Fragment>
                              );
                          })
                        : null}
                </div>
            </div>
        </BaseSystemPlugin>
    );
});

export default ContextMenu;
